// Purchase Requisition adapter: keeps the row shape the original screens read, backed by the API.
import {
  GetAllPurchaseRequisitionsApi,
  GetPurchaseRequisitionByIdApi,
  UpdatePurchaseRequisitionApprovalApi,
  EditPurchaseRequisitionApi,
  CancelPurchaseRequisitionLinesApi,
} from "../../../../Api/Procurement/PurchaseRequisition";

let lastLoginId = "";

const unwrap = (res) => {
  if (!res?.data?.IsSuccess) {
    throw new Error(res?.data?.Message || "Request failed");
  }
  return res.data.Data;
};

const fmtDate = (value) => {
  if (!value) {
    return "";
  }
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString("en-GB");
};

const buildHistory = (pr) => {
  const history = [
    // the MR requester raised it; CreatedByName is only the approver who triggered the PR
    {
      step: "Raised",
      by: pr.RequestedByName || pr.CreatedByName || "System",
      date: fmtDate(pr.CreatedAt),
    },
  ];
  (pr.Approvals || [])
    .filter((a) => a.Status !== "Pending")
    .forEach((a) =>
      history.push({
        step: a.Comments ? `${a.Status} (${a.Comments})` : a.Status,
        by: a.ApproverName,
        date: fmtDate(a.ActionDate),
      }),
    );
  (pr.PurchaseOrders || []).forEach((po) =>
    history.push({
      step: `PO ${po.PoNumber} (${po.Status})`,
      by: "Procurement",
      date: fmtDate(po.PoDate),
    }),
  );
  if (pr.CancelledReason) {
    history.push({
      step: `Cancelled: ${pr.CancelledReason}`,
      by: "", // the backend does not record who cancelled
      date: fmtDate(pr.UpdatedAt),
    });
  }
  return history;
};

const toRow = (pr, loginId) => {
  const lines = pr.Lines || [];
  const approvals = pr.Approvals || [];
  const pending = pr.Status === "Pending Approval";
  const hasOpen = lines.some((l) => l.OpenQuantity > 0);
  return {
    id: pr.Id,
    reqNo: pr.PrNumber,
    mrNumber: pr.MrNumber,
    project: pr.ProjectName,
    projectId: pr.ProjectId,
    requestedBy: pr.RequestedByName,
    dateRaised: fmtDate(pr.CreatedAt),
    requiredBy: fmtDate(pr.RequiredByDate),
    itemsSummary: lines
      .map((l) => `${l.MaterialName} (${l.Quantity} ${l.BaseUomName})`)
      .join(",\n"),
    // cancelled quantity no longer counts; ordered quantity still does
    estCost: lines.reduce((t, l) => t + (l.Quantity - l.CancelledQuantity) * l.EstimatedRate, 0),
    priority: pr.Priority || "Medium",
    approvalStatus: pr.Status,
    notes: pr.SpecificInstructions || pr.CancelledReason || "",
    items: lines.map((l) => ({
      id: l.Id,
      materialName: l.MaterialName,
      spec: l.Specification || "",
      qty: l.Quantity,
      uom: l.BaseUomName,
      unitPrice: l.EstimatedRate,
      totalCost: (l.Quantity - l.CancelledQuantity) * l.EstimatedRate,
      remarks: l.Purpose || "",
      openQty: l.OpenQuantity,
      orderedQty: l.OrderedQuantity,
      cancelledQty: l.CancelledQuantity,
    })),
    history: buildHistory(pr),
    approvals,
    approverIds: approvals.map((a) => a.ApproverUserId),
    canDecide:
      pending && approvals.some((a) => a.ApproverUserId === loginId && a.Status === "Pending"),
    canEdit: pending && !pr.HasDecision,
    canCancel: (pending || ["Approved", "Partially Ordered"].includes(pr.Status)) && hasOpen,
    _raw: pr,
  };
};

export const getMaterialRequisitions = async (loginId) => {
  lastLoginId = loginId || lastLoginId;
  const data = unwrap(await GetAllPurchaseRequisitionsApi());
  return data.map((pr) => toRow(pr, lastLoginId));
};

// Approve or reject the caller's own pending approval; returns the refreshed list.
export const updateRequisitionStatus = async (id, status, comments) => {
  const pr = unwrap(await GetPurchaseRequisitionByIdApi(id));
  const mine = (pr.Approvals || []).find(
    (a) => a.ApproverUserId === lastLoginId && a.Status === "Pending",
  );
  if (!mine) {
    throw new Error("You are not a pending approver of this requisition.");
  }
  unwrap(await UpdatePurchaseRequisitionApprovalApi(mine.Id, status, comments));
  return getMaterialRequisitions();
};

// Only approvers and per-line estimated rates are editable on a PR.
export const saveMaterialRequisition = async (req) => {
  if (!req?.id) {
    throw new Error("Purchase Requisitions are created from an approved Material Requisition.");
  }
  unwrap(
    await EditPurchaseRequisitionApi(req.id, {
      approverUserIds: req.approverIds || [],
      lines: (req.items || []).map((i) => ({
        lineId: i.id,
        estimatedRate: Number(i.unitPrice) || 0,
      })),
    }),
  );
  return getMaterialRequisitions();
};

// Cancels the whole PR (empty lines = everything still open).
export const deleteMaterialRequisition = async (id, reason) => {
  unwrap(await CancelPurchaseRequisitionLinesApi(id, { reason, lines: [] }));
  return getMaterialRequisitions();
};

// quantity is in base units.
export const cancelRequisitionLine = async (id, lineId, quantity, reason) => {
  unwrap(await CancelPurchaseRequisitionLinesApi(id, { reason, lines: [{ lineId, quantity }] }));
  return getMaterialRequisitions();
};

// Router state for AddPurchaseOrder, or { error }.
export const buildPoState = (rows) => {
  if (rows.some((r) => !["Approved", "Partially Ordered"].includes(r.approvalStatus))) {
    return { error: "Only Approved or Partially Ordered requisitions can be ordered." };
  }
  if (new Set(rows.map((r) => r.projectId)).size > 1) {
    return { error: "Select requisitions from one project." };
  }
  const fromPurchaseRequisitions = rows
    .map((r) => ({
      purchaseRequisitionId: r.id,
      prNumber: r.reqNo,
      projectId: r.projectId,
      projectName: r.project,
      lines: (r._raw.Lines || [])
        .filter((l) => l.OpenQuantity > 0)
        .map((l) => ({
          lineId: l.Id,
          materialId: l.MaterialId,
          materialName: l.MaterialName,
          baseUomName: l.BaseUomName,
          openQuantity: l.OpenQuantity,
          estimatedRate: l.EstimatedRate,
          packagings: l.Packagings || [],
        })),
    }))
    .filter((p) => p.lines.length > 0);
  if (fromPurchaseRequisitions.length === 0) {
    return { error: "Nothing is open to order on the selected requisitions." };
  }
  return { fromPurchaseRequisitions };
};
